// src/lib/downloadImages.js
//
// The client half of the download-quality work. It turns a list of stored
// images plus a quality tier into a list of named blobs, and reports honest
// byte-level progress while it does so.
//
// Bytes come from the image host directly wherever CORS allows it, so the load
// lands on the user's connection rather than on one shared server address. Two
// server endpoints back that up:
//   POST /api/image-variants  resolves each image to a real tier URL and, when
//                             it can, its size in bytes.
//   GET  /api/proxy-image     streams that URL back for hosts that send no CORS
//                             header, preserving the upstream Content-Length
//                             when one exists.
//
// Nothing here imports React. Task 10's dialog is the only consumer.

import { extensionFor, safeFilename } from './fileExt';

export const TIERS = [
  { id: 'standard', label: 'Standard', detail: '~800px' },
  { id: 'high', label: 'High', detail: '~2048px' },
  { id: 'max', label: 'Maximum', detail: 'original' },
];

export const DEFAULT_TIER = 'high';
const TIER_STORAGE_KEY = 'downloadQuality';

// Matches IMAGE_VARIANTS_MAX_IMAGES in server.js. A larger board is split into
// several sequential requests rather than being rejected outright: the server
// answers a batch over this size with a 400 and no variants at all.
export const VARIANTS_CHUNK_SIZE = 250;

// Proxy fetches run against one Node process that also serves the API and the
// static bundle, and a board can hold hundreds of images. An unbounded
// Promise.all opens one socket per image; this keeps a handful in flight.
const DOWNLOAD_CONCURRENCY = 5;

// A fast connection delivers chunks faster than React can render. Byte-level
// progress is coalesced to this interval; completions and the final state are
// always reported immediately so the bar lands exactly on full.
const PROGRESS_INTERVAL_MS = 100;

const isTier = (id) => TIERS.some((t) => t.id === id);

export function loadTier() {
  // Safari private mode throws on access rather than returning null.
  try {
    const stored = window.localStorage.getItem(TIER_STORAGE_KEY);
    return isTier(stored) ? stored : DEFAULT_TIER;
  } catch (err) {
    return DEFAULT_TIER;
  }
}

export function saveTier(tier) {
  if (!isTier(tier)) return;
  try {
    window.localStorage.setItem(TIER_STORAGE_KEY, tier);
  } catch (err) {
    // Persisting the preference is a convenience, not a requirement.
  }
}

// Returns 'unknown' rather than a number for a size the server could not
// determine. Rendering that as "0 MB" would tell the user an image is empty
// when in fact nobody has measured it -- see the bytes:null discussion below.
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return 'unknown';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;
  return `${mb.toFixed(1)} MB`;
}

// Bounded-parallelism map, positional results. A client-side twin of
// lib/concurrency.js: CRA refuses imports from outside src/, and that module is
// CommonJS. The shared cursor is read and incremented with no await between the
// two, so no two workers can ever claim the same index.
export async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await mapper(items[index], index);
    }
  };
  // Clamped on both ends: a limit of 0 or NaN would otherwise spawn no workers
  // at all and resolve to an array of holes with the mapper never invoked.
  const workers = Math.max(1, Math.min(limit, items.length) || 0);
  await Promise.all(Array.from({ length: workers }, worker));
  return results;
}

function isAbort(err, signal) {
  if (signal && signal.aborted) return true;
  return !!err && (err.name === 'AbortError' || err.code === 20);
}

function unresolvedVariant(index) {
  return { id: null, index, url: null, bytes: null, resolved: false };
}

/**
 * Resolve every image to a tier URL.
 *
 * The result is strictly positional: `variants[i]` describes `images[i]`,
 * whatever order the server emits. The join key is the server's own `index`,
 * never `id` -- session images and freshly fetched images usually have no id,
 * so the server returns `id: null` for all of them and keying a Map by id
 * collapses the whole board onto one entry.
 *
 * Boards larger than VARIANTS_CHUNK_SIZE are sent as several sequential
 * requests (the server numbers each batch from 0, so indices are rebased on
 * the way back in). Sequential rather than parallel: one batch already fans out
 * to 8 concurrent upstream lookups on the server and the endpoint is rate
 * limited to 30 requests a minute.
 *
 * A chunk that fails degrades to unresolved entries so the rest of the board
 * still downloads; only a total failure throws.
 *
 * @param {Array<object>} images
 * @param {'standard'|'high'|'max'} tier
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<{id: *, index: number, url: ?string, bytes: ?number, resolved: boolean}>>}
 */
export async function resolveVariants(images, tier, signal) {
  if (!Array.isArray(images) || images.length === 0) return [];

  const out = new Array(images.length);

  for (let start = 0; start < images.length; start += VARIANTS_CHUNK_SIZE) {
    const chunk = images.slice(start, start + VARIANTS_CHUNK_SIZE);
    // Only the fields the server reads. Sending whole image records would blow
    // the 512kb body limit long before the 250-image cap.
    const payload = chunk.map((image) => ({
      id: image && image.id != null ? image.id : null,
      url: image && image.url,
      thumbnailUrl: image && image.thumbnailUrl,
      fullSizeUrl: image && image.fullSizeUrl,
      source: image && image.source,
    }));

    let variants = null;
    try {
      const res = await fetch('/api/image-variants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: payload, tier }),
        signal,
      });
      if (!res.ok) throw new Error(`image-variants failed: HTTP ${res.status}`);
      const body = await res.json();
      if (!body || !Array.isArray(body.variants)) throw new Error('image-variants returned no variants');
      variants = body.variants;
    } catch (err) {
      // A cancelled download is not a degraded one.
      if (isAbort(err, signal)) throw err;
      variants = null;
    }

    for (let i = 0; i < chunk.length; i += 1) out[start + i] = unresolvedVariant(start + i);

    if (variants) {
      variants.forEach((variant, position) => {
        if (!variant || typeof variant !== 'object') return;
        // `index` is authoritative; position is only a fallback for a server
        // that somehow omitted it.
        const local = Number.isInteger(variant.index) ? variant.index : position;
        if (local < 0 || local >= chunk.length) return;
        const bytes = Number.isFinite(variant.bytes) && variant.bytes >= 0 ? variant.bytes : null;
        out[start + local] = {
          id: variant.id != null ? variant.id : null,
          index: start + local,
          url: variant.url || null,
          bytes,
          resolved: variant.resolved === true,
        };
      });
    }
  }

  // Deliberately not an error. `out` is already filled with unresolvedVariant,
  // and candidatesFor falls back to fullSizeUrl then thumbnailUrl -- exactly what
  // Dashboard and SessionReview did before this endpoint existed. Throwing here
  // would make a brand-new endpoint a single point of failure for a download
  // path that used to work without it; the user loses the quality upgrade, not
  // the download. `unresolved` in the result reports the degradation.
  return out;
}

// Open one image, preferring a direct cross-origin fetch and falling back to
// the proxy.
//
// Direct first because the proxy is a single datacenter IP shared by every
// user, and at download volume the big hosts throttle it: measured in
// production, upload.wikimedia.org answered the proxy with a persistent 429 and
// live.staticflickr.com with a 502, while the identical requests from a browser
// succeeded. Both send `Access-Control-Allow-Origin: *`, so the browser can
// fetch them itself, from the user's own address, spreading the load the way
// thumbnails already are -- which is precisely why thumbnails kept working
// while downloads failed.
//
// The proxy stays as the fallback, not as dead weight: archive.org sends no
// CORS header at all and can only be reached that way, and a host that drops
// its header later degrades to the old behaviour instead of breaking.
async function openImage(sourceUrl, signal) {
  let directReason;
  try {
    const res = await fetch(sourceUrl, { signal, credentials: 'omit' });
    if (res.ok) return res;
    directReason = `HTTP ${res.status}`;
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    directReason = failureReason(err);
  }

  const res = await fetch(`/api/proxy-image?url=${encodeURIComponent(sourceUrl)}`, { signal });
  if (res.ok) return res;

  // Both transports are named. Without that, a report reading "HTTP 429" leaves
  // the reader unable to tell a host that is blocking everyone from a host that
  // is blocking only us -- which is the difference between a broken image and a
  // broken deployment.
  const err = new Error(`direct ${directReason}, proxy HTTP ${res.status}`);
  err.status = res.status;
  try {
    // The proxy answers every refusal with {error}. Carrying it through is the
    // difference between "HTTP 400" and "HTTP 400: Image URL resolves to a
    // private address", which is the whole point of the report.
    const body = await res.json();
    if (body && body.error) err.message += `: ${body.error}`;
  } catch (parseErr) {
    // A non-JSON error body is not worth failing over.
  }
  throw err;
}

// Stream one image, reporting bytes as they arrive.
//
// `sourceUrl` is passed to the caller untouched: extensionFor must see the real
// URL, because '/api/proxy-image?url=...' has no dot after its last slash and
// would silently fall through to 'jpg' for every PNG on the board.
async function fetchWithProgress(sourceUrl, { signal, onBytes, onSize }) {
  const res = await openImage(sourceUrl, signal);

  const contentType = (res.headers && res.headers.get('content-type')) || 'image/jpeg';

  // A correction to a HEAD probe that came back empty, readable on both
  // transports: proxyImage.js refuses to forward a length alongside a
  // Content-Encoding, and Content-Length is CORS-safelisted so a direct
  // cross-origin fetch exposes it too. A compressed direct response reports the
  // compressed count while the body arrives decompressed, which is why the
  // progress maths takes max(advertised, loaded) rather than trusting this.
  if (onSize && res.headers) {
    const advertised = Number.parseInt(res.headers.get('content-length'), 10);
    if (Number.isFinite(advertised) && advertised >= 0) onSize(advertised);
  }

  // Old Safari has no readable stream on the response. Correctness over
  // granularity: the file still downloads, it just arrives in one step.
  if (!res.body || typeof res.body.getReader !== 'function') {
    const blob = await res.blob();
    onBytes(blob.size);
    return blob;
  }

  const reader = res.body.getReader();
  const chunks = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    onBytes(value.length);
  }
  return new Blob(chunks, { type: contentType });
}

// The URLs worth trying for one image, best first, de-duplicated.
//
// `image.url` is deliberately absent. archive.js stores it as the human-facing
// page (archive.org/details/..., flickr.com/photos/...), so fetching it would
// hand the user an HTML document named .jpg. Every existing download path in
// Dashboard.js and SessionReview.js uses fullSizeUrl || thumbnailUrl for the
// same reason.
// Each candidate carries the name of the field it came from, so a failure
// report can say *which* URL died rather than printing three lookalike CDN
// paths and leaving the reader to diff them by eye.
function candidatesFor(image, variant) {
  const seen = new Set();
  const out = [];
  [
    ['tier', variant && variant.url],
    ['fullSizeUrl', image && image.fullSizeUrl],
    ['thumbnailUrl', image && image.thumbnailUrl],
  ].forEach(([kind, url]) => {
    if (typeof url === 'string' && url && !seen.has(url)) {
      seen.add(url);
      out.push({ kind, url });
    }
  });
  return out;
}

// Turn whatever fetchWithProgress threw into one short human-readable line.
//
// TypeError is what fetch() rejects with for DNS failure, connection refused, a
// CORS block, or the browser cutting off a request -- all indistinguishable
// from JS by design, so the message says what is actually knowable rather than
// inventing a cause.
function failureReason(err) {
  if (!err) return 'unknown error';
  if (err.name === 'TypeError') return 'network error (blocked, offline, or DNS)';
  return err.message || String(err);
}

// Splices the disambiguator in *after* safeFilename has truncated to 50
// characters. Appending it to the title beforehand loses it to that truncation,
// so a board of long identically-titled images produced duplicate names -- and
// JSZip silently overwrites a duplicate entry, so the user quietly received
// fewer files than they asked for.
function uniqueFilename(title, extension, ordinal) {
  const base = safeFilename(title, extension);
  const stem = base.slice(0, base.length - extension.length - 1);
  return `${stem}_${ordinal}.${extension}`;
}

/**
 * Download every image at the requested tier.
 *
 * Progress (`onProgress`) receives:
 *   {
 *     completed:    number,  // images finished, successfully or not
 *     total:        number,
 *     bytesLoaded:  number,
 *     bytesTotal:   number,  // see below -- always >= bytesLoaded
 *     bytesUnknown: number,  // unfinished images with no size yet
 *     fraction:     number,  // 0..1, never above 1
 *   }
 *
 * How D2 (`bytes: null`) is handled. The server leaves `bytes` null whenever
 * the HEAD probe produced no length -- every *.staticflickr.com URL, plus any
 * host that transiently 429s. So the up-front total systematically
 * under-counts, and dividing by it naively runs the bar past 100%.
 *
 * `bytesTotal` is therefore recomputed on every report rather than fixed up
 * front. An image that has not finished contributes max(advertised, loaded), so
 * a size the server under-reported cannot push the fraction above 1; an image
 * that has finished contributes exactly what it delivered, so once the board is
 * done bytesTotal === bytesLoaded and the bar lands on full by construction
 * rather than by clamping. `bytesUnknown` counts the images still missing a
 * size, so Task 10 can render "~12 MB + 8 of unknown size" instead of implying
 * a precision that does not exist. Sizes also improve mid-flight: the proxy
 * forwards the upstream Content-Length, which frequently exists on the GET even
 * when the HEAD gave nothing.
 *
 * How D8 (`resolved: false`) is surfaced. Each returned file carries
 * `tierResolved`, true only when the image was actually delivered from the
 * resolved tier URL -- an unresolved variant, or a tier URL that failed and
 * fell back, both read false. `unresolved` is the count of delivered files with
 * tierResolved false, so the dialog can say "3 of 20 images were only available
 * at their original size" instead of silently handing over a thumbnail.
 *
 * @param {Array<object>} images
 * @param {'standard'|'high'|'max'} tier
 * @param {{signal?: AbortSignal, onProgress?: function}} [options]
 * @returns {Promise<{
 *   files: Array<{blob: Blob, name: string, index: number, tierResolved: boolean}>,
 *   total: number,
 *   failed: number,
 *   unresolved: number,
 * }>}
 */
/**
 * Render `failures` as plain text for the clipboard.
 *
 * Deliberately verbatim about URLs: the whole reason this exists is that a
 * failure count alone was unactionable, and a truncated or prettified URL is
 * just as unactionable. Includes the tier and a timestamp because the same
 * image can fail at `max` and succeed at `standard`, and that is the single
 * most useful thing to know when reading a pasted report.
 */
export function formatFailureReport(failures, tier, total) {
  const list = Array.isArray(failures) ? failures : [];
  const lines = [
    `Endotica download report`,
    `when:  ${new Date().toISOString()}`,
    `tier:  ${tier}`,
    `failed: ${list.length}${typeof total === 'number' ? ` of ${total}` : ''}`,
    '',
  ];

  list.forEach((failure) => {
    lines.push(`#${failure.index + 1} "${failure.title}"  [${failure.source}]`);
    lines.push(`  tier URL resolved: ${failure.variantResolved ? 'yes' : 'no'}`);
    if (!failure.attempts || failure.attempts.length === 0) {
      lines.push('  no URLs to try (no fullSizeUrl, no thumbnailUrl)');
    } else {
      failure.attempts.forEach((attempt) => {
        lines.push(`  ${attempt.kind}: ${attempt.reason}`);
        lines.push(`    ${attempt.url}`);
      });
    }
    lines.push('');
  });

  return lines.join('\n');
}

export async function downloadAtTier(images, tier, options = {}) {
  const { signal, onProgress } = options || {};
  const list = Array.isArray(images) ? images : [];
  const total = list.length;

  if (total === 0) return { files: [], total: 0, failed: 0, unresolved: 0 };

  const variants = await resolveVariants(list, tier, signal);

  // One entry per image: `advertised` is the best known size (null = unknown),
  // `loaded` is bytes received for the *current* attempt only.
  const entries = list.map((_image, index) => ({
    advertised: variants[index] ? variants[index].bytes : null,
    loaded: 0,
    done: false,
  }));

  let completed = 0;
  let lastReportAt = 0;

  const snapshot = () => {
    let bytesLoaded = 0;
    let bytesTotal = 0;
    let bytesUnknown = 0;
    entries.forEach((entry) => {
      bytesLoaded += entry.loaded;
      if (entry.done) {
        bytesTotal += entry.loaded;
      } else {
        bytesTotal += Math.max(entry.advertised || 0, entry.loaded);
        if (entry.advertised == null) bytesUnknown += 1;
      }
    });
    const fraction = bytesTotal > 0
      ? Math.min(1, bytesLoaded / bytesTotal)
      : completed / total;
    return { completed, total, bytesLoaded, bytesTotal, bytesUnknown, fraction };
  };

  const report = (force) => {
    if (typeof onProgress !== 'function') return;
    const now = Date.now();
    if (!force && now - lastReportAt < PROGRESS_INTERVAL_MS) return;
    lastReportAt = now;
    onProgress(snapshot());
  };

  report(true);

  // Once one image aborts, the remaining workers must not start new fetches.
  let abortError = null;

  // Written from several concurrent workers, so it comes back in completion
  // order rather than board order; sorted by index before it is returned.
  const failures = [];

  const results = await mapWithConcurrency(list, DOWNLOAD_CONCURRENCY, async (image, index) => {
    if (abortError) throw abortError;
    if (signal && signal.aborted) {
      abortError = new DOMException('Aborted', 'AbortError');
      throw abortError;
    }

    const variant = variants[index];
    const entry = entries[index];
    const candidates = candidatesFor(image, variant);
    const tierUrl = variant && variant.resolved ? variant.url : null;

    const attempts = [];

    for (const { kind, url } of candidates) {
      // A failed attempt must leave no bytes behind: its partial transfer is
      // already on the counter, and letting the next candidate add on top makes
      // progress overshoot and never recover.
      entry.loaded = 0;
      try {
        const blob = await fetchWithProgress(url, {
          signal,
          onBytes: (n) => { entry.loaded += n; report(false); },
          onSize: (n) => { entry.advertised = n; },
        });
        entry.done = true;
        completed += 1;
        report(true);
        const extension = extensionFor(blob.type, url);
        const title = image.title || image.name || '';
        return {
          blob,
          name: uniqueFilename(title, extension, index + 1),
          index,
          tierResolved: url === tierUrl,
        };
      } catch (err) {
        if (isAbort(err, signal)) {
          abortError = err;
          throw err;
        }
        attempts.push({ kind, url, reason: failureReason(err) });
        entry.loaded = 0;
      }
    }

    entry.loaded = 0;
    entry.done = true;
    completed += 1;
    report(true);
    failures.push({
      index,
      title: (image && (image.title || image.name)) || '(untitled)',
      source: (image && image.source) || 'unknown',
      // Empty means there was nothing to try: the row carries neither a
      // fullSizeUrl nor a thumbnailUrl and the variant did not resolve one.
      // That is a data problem, not a network one, and the report has to say
      // so or it reads as a silent failure with no explanation at all.
      attempts,
      variantResolved: !!(variant && variant.resolved),
    });
    return null;
  });

  const files = results.filter(Boolean);
  report(true);

  return {
    files,
    total,
    failed: total - files.length,
    unresolved: files.filter((file) => !file.tierResolved).length,
    failures: failures.sort((a, b) => a.index - b.index),
  };
}
